const DAY_MS = 86_400_000;
// UTC instant whose wall-clock time in `timezone` is `dayKey` (YYYY-MM-DD) at
// hour:minute. Treats the wall time as UTC then shifts by the zone's offset at
// that instant. Used to turn a coarse time-of-day into a concrete delivery moment.
export function utcForLocalTime(dayKey, hour, minute, timezone = "UTC") {
    const tz = timezone ?? "UTC";
    const hh = String(hour).padStart(2, "0");
    const mm = String(minute).padStart(2, "0");
    const naiveUtc = new Date(`${dayKey}T${hh}:${mm}:00Z`);
    if (Number.isNaN(naiveUtc.getTime()))
        return naiveUtc;
    try {
        const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: tz,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        }).formatToParts(naiveUtc);
        const f = (t) => Number(parts.find((p) => p.type === t)?.value ?? "0");
        const wallHour = f("hour") === 24 ? 0 : f("hour");
        const asTzWall = Date.UTC(f("year"), f("month") - 1, f("day"), wallHour, f("minute"), f("second"));
        return new Date(naiveUtc.getTime() - (asTzWall - naiveUtc.getTime()));
    }
    catch {
        return naiveUtc;
    }
}
// Postgres rejects unknown zone names with an error, unlike the Intl fallback
// convention elsewhere; validate before interpolating a timezone into SQL so a
// bad stored zone degrades to UTC day-bucketing instead of a query error.
export function resolvableTimezone(timezone) {
    if (!timezone)
        return "UTC";
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: timezone });
        return timezone;
    }
    catch {
        return "UTC";
    }
}
export function localDateKey(date, timezone = "UTC") {
    try {
        const parts = new Intl.DateTimeFormat("en-CA", {
            timeZone: timezone ?? "UTC",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).formatToParts(date);
        const year = parts.find((p) => p.type === "year")?.value;
        const month = parts.find((p) => p.type === "month")?.value;
        const day = parts.find((p) => p.type === "day")?.value;
        if (year && month && day)
            return `${year}-${month}-${day}`;
    }
    catch {
        // Fall back to UTC for malformed or unsupported IANA zones.
    }
    return date.toISOString().slice(0, 10);
}
// Intl parts, not a toLocaleString round-trip: the round-trip parses the
// string back as the server's local time and drifts an hour around DST.
// Falls back to UTC wall-clock for malformed or unsupported IANA zones so a
// bad stored timezone can never throw out of a scheduler or pipeline tick.
export function minutesSinceLocalMidnight(now, timezone) {
    try {
        const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: timezone,
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        }).formatToParts(now);
        const hh = Number.parseInt(parts.find((p) => p.type === "hour")?.value ?? "0", 10);
        const mm = Number.parseInt(parts.find((p) => p.type === "minute")?.value ?? "0", 10);
        return (hh % 24) * 60 + mm;
    }
    catch {
        return now.getUTCHours() * 60 + now.getUTCMinutes();
    }
}
export function localPartOfDay(date, timezone = "UTC") {
    let hour;
    try {
        hour = Number(new Intl.DateTimeFormat("en-US", {
            timeZone: timezone ?? "UTC",
            hour: "2-digit",
            hour12: false,
        }).format(date));
    }
    catch {
        hour = date.getUTCHours();
    }
    return hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
}
// Human-readable local clock for agent prompts, e.g. "afternoon (around
// 4:27 PM)". The window word is what the models key on; the clock grounds it.
export function localTimeLabel(date, timezone = "UTC") {
    try {
        const tz = timezone ?? "UTC";
        const clock = new Intl.DateTimeFormat("en-US", {
            timeZone: tz,
            hour: "numeric",
            minute: "2-digit",
        }).format(date);
        return `${localPartOfDay(date, tz)} (around ${clock})`;
    }
    catch {
        return "unknown";
    }
}
/** Normalises a DB date value (string, Date, null, or undefined) to a
 *  YYYY-MM-DD key in UTC. Returns null for missing or unparseable input.
 *  Use this instead of copying the same 5-line pattern across files. */
export function dateKeyOf(input) {
    if (!input)
        return null;
    if (input instanceof Date)
        return input.toISOString().slice(0, 10);
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(input);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
}
export function dateKeyOrdinal(key) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(key);
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day))
        return null;
    return Math.floor(Date.UTC(year, month - 1, day) / DAY_MS);
}
export function daysBetweenDateKeys(later, earlier) {
    const laterOrdinal = dateKeyOrdinal(later);
    const earlierOrdinal = dateKeyOrdinal(earlier);
    if (laterOrdinal == null || earlierOrdinal == null)
        return null;
    return laterOrdinal - earlierOrdinal;
}
export function addDaysToDateKey(key, days) {
    const ordinal = dateKeyOrdinal(key);
    if (ordinal == null)
        return key;
    return new Date((ordinal + days) * DAY_MS).toISOString().slice(0, 10);
}
export function weekdayIndexForDateKey(key) {
    const ordinal = dateKeyOrdinal(key);
    if (ordinal == null)
        return 1;
    return new Date(ordinal * DAY_MS).getUTCDay();
}
/** Formats a fractional number of hours as a compact "Xh Ym" string.
 *  Used for sleep, meal timing, and other duration displays.
 *  E.g. 7.5 → "7h 30m", 0.25 → "0h 15m". */
export function formatHoursMinutes(hours) {
    return `${Math.floor(hours)}h ${Math.round((hours % 1) * 60)}m`;
}
/** Formats a fractional number of hours as a compact duration string, dropping
 *  whichever half says nothing: "0m" on a whole hour, "0h" under one.
 *  Rounds total minutes first to avoid the edge case where
 *  Math.round(fractional * 60) produces 60 for inputs like 2.9999.
 *  E.g. 7.5 → "7h 30m", 8 → "8h", 0.967 → "58m", 2.9999 → "3h". */
export function formatHours(hours) {
    const totalMinutes = Math.round(hours * 60);
    const wholeHours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (wholeHours === 0)
        return `${minutes}m`;
    return minutes === 0 ? `${wholeHours}h` : `${wholeHours}h ${minutes}m`;
}
//# sourceMappingURL=local-date.js.map